
























































import Vue, { PropType, VueConstructor } from "vue";
import {
  CalDavConfigurationForm,
  createCalDavConfigurationForm,
} from "@/models/providerAccount";
import validationMixin, {
  ValidatableForm,
} from "@/components/mixins/validationMixin";
import { translateOr } from "@/services/externalProvidersService";

type ValidationRule = (value: string) => true | string;

interface ValidationMixin extends Vue {
  valid: boolean;
  required: ValidationRule;
}

/**
 * Connecting a Live Collaboration account: no OAuth, only the username and password — the
 * server is ours (LIVE_COLLABORATION_CALENDAR_URL), and the room reads the account's BlueMind
 * calendar over CalDAV with it. The password is never pre-filled, and it is cleared from the
 * form as soon as the dialog closes (kept only after a refusal, to retry).
 */
export default (Vue as VueConstructor<ValidationMixin>).extend({
  name: "LiveCollaborationConfigurationDialog",
  mixins: [validationMixin],
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    initialConfig: {
      type: Object as PropType<CalDavConfigurationForm>,
      default: createCalDavConfigurationForm,
    },
    callback: {
      type: Function as PropType<
        (config: CalDavConfigurationForm) => Promise<unknown> | void
      >,
      required: false,
    },
  },
  data() {
    return {
      form: { ...this.initialConfig } as CalDavConfigurationForm,
      saving: false,
    };
  },
  computed: {
    dialogOpen: {
      get(): boolean {
        return this.value;
      },
      set(open: boolean) {
        if (!open) {
          this.onCancel();
        }
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.resetForm();
        }
      },
    },
  },
  methods: {
    label(key: string, fallback: string): string {
      return translateOr(key, fallback);
    },
    /** `required`, on the value that will be saved (trimmed). */
    notBlank(value: string): true | string {
      return this.required((value || "").trim());
    },
    resetForm() {
      this.form = {
        ...createCalDavConfigurationForm(),
        ...this.initialConfig,
        password: "",
      };
      (
        this.$refs.liveCollaborationConfigurationForm as
          | ValidatableForm
          | undefined
      )?.resetValidation();
    },
    onCancel() {
      // Not kept in memory once the dialog is closed.
      this.form.password = "";
      this.$emit("input", false);
      this.$emit("cancel");
    },
    async save() {
      if (this.saving) return;
      if (
        !(
          this.$refs.liveCollaborationConfigurationForm as ValidatableForm
        ).validate()
      ) {
        return;
      }
      const config: CalDavConfigurationForm = {
        username: this.form.username.trim(),
        password: this.form.password,
      };
      this.saving = true;
      try {
        if (this.callback) {
          await this.callback(config);
        }
      } catch {
        // The caller has shown why; the dialog stays open with what was typed.
        return;
      } finally {
        this.saving = false;
      }
      this.form.password = "";
      this.$emit("submit", config);
      this.$emit("input", false);
    },
  },
});
